import Listbox from '@oncolens/oncolens-ui/lib/components/FormControls/Listbox';
import FormField from '@oncolens/oncolens-ui/lib/libraries/FormField';
import TextInput from '@oncolens/oncolens-ui/lib/components/FormControls/TextInput';
import { useConfigs } from '@oncolens/oncolens-ui/lib/hooks/common/useConfig';
import RequestSection from '../../RequestSection';
import store from './store';
import shallow from 'zustand/shallow';
import DatePicker from '@oncolens/oncolens-ui/lib/components/FormControls/DatePicker';
import RadioGroup from '@oncolens/oncolens-ui/lib/components/FormControls/RadioGroup';
import clsx from 'clsx';
import { FaRegCheckCircle, FaRegCircle } from 'react-icons/fa';
import Checkbox from '@oncolens/oncolens-ui/lib/components/FormControls/Checkbox';
import TextAreaInput from "@oncolens/oncolens-ui/lib/components/FormControls/TextAreaInput";

function PatientSection() {
  const [getValues] = store((state) => [state.getValues], shallow);
  const formKey = 'patient';
  const configs = useConfigs(['sex', 'event_history_status']);

  return (
    <RequestSection title="3. Provide the relevant clinical information">
      <div className="grid grid-cols-1 gap-3 mb-6 md:grid-cols-2">
        <FormField name={`${formKey}.sexId`} store={store}>
          {({ value, onChange, errorMessage }) => (
            <Listbox
              label="Sex"
              required={true}
              data={configs?.sex || []}
              error={errorMessage}
              selected={value}
              setSelected={onChange}
            />
          )}
        </FormField>

        <FormField name={`${formKey}.age`} store={store}>
          {({ value, onChange, errorMessage }) => (
            <TextInput
              id="age"
              label="Age"
              htmlName="age"
              placeholder="Please enter the patient's age"
              type="number"
              required={true}
              error={errorMessage}
              value={value}
              onChange={onChange}
            />
          )}
        </FormField>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <FormField
          name={`${formKey}.dateOfLastBiopsy`}
          store={store}
          validationOptions={{
            valueProvider: getValues,
          }}
        >
          {({ value, onChange, errorMessage }) => (
            <DatePicker
              label="Date of Last Biopsy"
              id={'dateOfLastBiopsy'}
              htmlName={'dateOfLastBiopsy'}
              required={true}
              error={errorMessage}
              selected={value}
              setSelected={onChange}
              inputProps={{ maxDate: new Date() }}
            />
          )}
        </FormField>

        <div className="mb-6 md:ml-4">
          <label htmlFor={'1'} className="inline-block mb-1 text-sm font-medium text-gray-700">
            Is Tissue Available?<span className="ml-1 text-red-500">*</span>
          </label>
          <FormField name={`${formKey}.isTissueAvailable`} store={store}>
            {({ value, onChange }) => (
              <RadioGroup
                className="flex items-center mb-5 space-x-2"
                selected={value}
                required={true}
                setSelected={onChange}
              >
                <RadioGroup.Option id={1}>
                  {(active, onClick) => (
                    <div
                      data-cy="is-tissue-available-yes"
                      className={clsx(
                        'flex items-center w-36 px-4 py-2 font-semibold text-sm bg-white border rounded-lg shadow-md cursor-pointer border-oncolensOrange-500 transition-colors',
                        active &&
                          'bg-oncolensBlue-500 text-white border-oncolensBlue-500 focus:ring-oncolensBlue-500 focus:border-oncolensBlue-500'
                      )}
                      onClick={() => {
                        onClick();
                      }}
                      onKeyDown={() => onClick()}
                      role="radiogroup"
                      tabIndex={-1}
                    >
                      {active ? (
                        <FaRegCheckCircle className="mr-2 text-white" />
                      ) : (
                        <FaRegCircle className="mr-2 text-oncolensOrange-500" />
                      )}
                      <p>Yes</p>
                    </div>
                  )}
                </RadioGroup.Option>
                <RadioGroup.Option id={0}>
                  {(active, onClick) => (
                    <div
                      data-cy="is-tissue-available-no"
                      className={clsx(
                        'flex items-center w-36 px-4 py-2 font-semibold text-sm bg-white border rounded-lg shadow-md cursor-pointer border-oncolensOrange-500 transition-colors',
                        active &&
                          'bg-oncolensBlue-500 text-white border-oncolensBlue-500 focus:ring-oncolensBlue-500 focus:border-oncolensBlue-500'
                      )}
                      onClick={() => {
                        onClick();
                      }}
                      onKeyDown={() => onClick()}
                      role="radiogroup"
                      tabIndex={-1}
                    >
                      {active ? (
                        <FaRegCheckCircle className="mr-2 text-white" />
                      ) : (
                        <FaRegCircle className="mr-2 text-oncolensOrange-500" />
                      )}
                      <p>No</p>
                    </div>
                  )}
                </RadioGroup.Option>
              </RadioGroup>
            )}
          </FormField>
        </div>

        <FormField name={`${formKey}.biopsyCollectionSite`} store={store}>
          {({ value, onChange, errorMessage }) => (
            <TextInput
              id="biopsyCollectionSite"
              label="Biopsy Collection Site"
              htmlName="biopsyCollectionSite"
              data-cy="biopsy-collection-site"
              type="text"
              required={true}
              error={errorMessage}
              value={value}
              onChange={onChange}
            />
          )}
        </FormField>
      </div>
      <div className="grid items-center gap-3 sm:grid-cols-3 mb-4">
        <FormField name={`${formKey}.pathologyText`} store={store}>
          {({ value, onChange, errorMessage }) => (
            <TextAreaInput
              id="pathologyText"
              label="Pathology Comments"
              htmlName="pathologyText"
              data-cy="pathology-text"
              required={true}
              error={errorMessage}
              value={value}
              onChange={onChange}
              wrapperProps={{ className: 'sm:col-span-3' }}
            />
          )}
        </FormField>
      </div>
      <div className="grid grid-cols-1 gap-3 mb-6 md:grid-cols-2">
       
        <FormField name={`${formKey}.pathologyStatus`} store={store}>
          {({ value, onChange, errorMessage }) => (
            <Listbox
              label="Pathology Status"
              data-cy="pathology-status"
              required={true}
              data={configs?.event_history_status || []}
              error={errorMessage}
              selected={value}
              setSelected={onChange}
            />
          )}
        </FormField>
        <FormField
          name={`${formKey}.pathologyDate`}
          store={store}
          validationOptions={{
            valueProvider: getValues,
          }}
        >
          {({ value, onChange, errorMessage }) => (
            <DatePicker
              label="Pathology Date"
              id={'pathologyDate'}
              htmlName={'pathologyDate'}
              data-cy="pathology-date"
              required={false}
              error={errorMessage}
              selected={value}
              setSelected={onChange}
              inputProps={{ maxDate: new Date() }}
            />
          )}
        </FormField>
      </div>

      <label htmlFor={'1'} className="block mb-4 font-medium text-gray-700">
        Other Co-morbid illnesses (select all that apply)
      </label>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3 lg:grid-cols-5">
          <FormField name="submitTo.autoImmuneDisease" store={store}>
            {({ onChange, value }) => (
              <Checkbox
                id="autoImmuneDisease"
                htmlName="autoImmuneDisease"
                label="Auto immune disease"
                value={value}
                inputProps={{ onChange: onChange, checked: value }}
              />
            )}
          </FormField>

          <FormField name="submitTo.uncontrolledDiabetes" store={store}>
            {({ onChange, value }) => (
              <Checkbox
                id="uncontrolledDiabetes"
                htmlName="uncontrolledDiabetes"
                label="Uncontrolled Diabetes"
                value={value}
                inputProps={{ onChange: onChange, checked: value }}
              />
            )}
          </FormField>

          <FormField name="submitTo.activeInfection" store={store}>
            {({ onChange, value }) => (
              <Checkbox
                id="activeInfection"
                htmlName="activeInfection"
                label="Active infection"
                value={value}
                inputProps={{ onChange: onChange, checked: value }}
              />
            )}
          </FormField>

          <FormField name="submitTo.dvtpe" store={store}>
            {({ onChange, value }) => (
              <Checkbox
                id="dvtpe"
                htmlName="dvtpe"
                label="DVT/PE"
                value={value}
                inputProps={{ onChange: onChange, checked: value }}
              />
            )}
          </FormField>

          <FormField name="submitTo.lowEjectionFraction" store={store}>
            {({ onChange, value }) => (
              <Checkbox
                id="lowEjectionFraction"
                htmlName="lowEjectionFraction"
                label="Low ejection fraction"
                value={value}
                inputProps={{ onChange: onChange, checked: value }}
              />
            )}
          </FormField>

          <FormField name="submitTo.hepatitis" store={store}>
            {({ onChange, value }) => (
              <Checkbox
                id="hepatitis"
                htmlName="hepatitis"
                label="Hepatitis"
                value={value}
                inputProps={{ onChange: onChange, checked: value }}
              />
            )}
          </FormField>

          <FormField name="submitTo.hiv" store={store}>
            {({ onChange, value }) => (
              <Checkbox
                id="hiv"
                htmlName="hiv"
                label="HIV"
                value={value}
                inputProps={{ onChange: onChange, checked: value }}
              />
            )}
          </FormField>

          <FormField name="submitTo.highQt" store={store}>
            {({ onChange, value }) => (
              <Checkbox
                id="highQt"
                htmlName="highQt"
                label="High QT"
                value={value}
                inputProps={{ onChange: onChange, checked: value }}
              />
            )}
          </FormField>

        <FormField name="submitTo.brainMetastasis" store={store}>
          {({ onChange, value }) => (
            <Checkbox
              id="brainMetastasis"
              htmlName="brainMetastasis"
              label="Brain Metastasis"
              value={value}
              inputProps={{ onChange: onChange, checked: value }}
            />
          )}
        </FormField>
        </div>
    </RequestSection>
  );
}

export default PatientSection;
